/**
 * TTS API Service
 *
 * Splits text into short clause-level chunks for fast first-audio playback.
 * Fetches audio with a sliding window (3 concurrent) and enqueues in order
 * so playback starts as soon as the first chunk arrives.
 *
 * Uses the /tts endpoint with Firebase authentication.
 */

import { enqueueAudio, resetAudioQueue, stopAudioQueue } from './audioQueue'
import { getIdToken } from './auth'
import { PRIVATE_MODE, PRIVATE_TTS_VOICE, PRIVATE_TTS_MODEL } from './privateConfig'
import { API_BASE } from './backendConfig'
const MAX_CONCURRENT_TTS = 3

export interface TtsSettings {
  /** The persona's TTS provider, as the app syncs it: gemini, geminiFlash, openai. */
  provider?: string
  voice?: string
  instructions?: string
  speed?: number
  language?: string  // e.g. "en-US", "fi-FI"
}

/** A persona's TTS provider as the backend's service, model and the call's
 *  speech format - the PiPar app's (Config.ttsServiceOf, getCurrentTTSModel,
 *  getAudioFormatForProvider). The Gemini models are the app's tier aliases:
 *  the backend resolves them to the model it currently points each tier at. */
export function ttsEngine(provider?: string): { service: string; model: string; callFormat: 'adpcm' | 'pcm' } {
  switch (provider) {
    case 'openai':
      return { service: 'openai', model: 'gpt-4o-mini-tts', callFormat: 'pcm' }
    case 'geminiFlash':
      return { service: 'gemini', model: 'gemini-flash-tts', callFormat: 'adpcm' }
    default: // gemini, and a self-hosted server's own voice (not on this backend)
      return { service: 'gemini', model: 'gemini-pro-tts', callFormat: 'adpcm' }
  }
}

/**
 * Split text into short, speakable chunks for faster first-audio.
 */
function splitIntoChunks(text: string): string[] {
  const sentences = text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean)

  const chunks: string[] = []
  for (const sentence of sentences) {
    if (sentence.length <= 80) {
      chunks.push(sentence)
      continue
    }
    const parts = sentence.split(/(?<=[,;:–—])\s+/)
    let current = ''
    for (const part of parts) {
      if (current.length + part.length > 80 && current.length >= 30) {
        chunks.push(current.trim())
        current = part
      } else {
        current += (current ? ' ' : '') + part
      }
    }
    if (current.trim()) {
      chunks.push(current.trim())
    }
  }

  return chunks.filter((c) => c.replace(/[\p{Emoji}\s.…!?]+/gu, '').length > 0)
}

async function fetchChunkAudio(
  chunk: string,
  chatId: string,
  settings: TtsSettings,
): Promise<string | null> {
  try {
    const token = await getIdToken()
    if (!token) return null

    const ttsBody = PRIVATE_MODE
      ? {
          text: chunk,
          chat_id: chatId,
          voice: PRIVATE_TTS_VOICE || settings.voice || null,
          format: 'wav',
          ...(PRIVATE_TTS_MODEL ? { model: PRIVATE_TTS_MODEL } : {}),
        }
      : {
          text: chunk,
          chat_id: chatId,
          service: ttsEngine(settings.provider).service,
          model: ttsEngine(settings.provider).model,
          voice: settings.voice || null,
          format: 'mp3',
          instructions: settings.instructions || 'Speak naturally.',
          speed: settings.speed || 1.0,
          language_code: settings.language || null,
        }

    const res = await fetch(`${API_BASE}/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(ttsBody),
    })

    if (!res.ok) {
      console.warn(`TTS fetch failed (${res.status}):`, await res.text().catch(() => ''))
      return null
    }

    const blob = await res.blob()
    if (blob.size === 0) {
      console.warn('TTS returned empty audio')
      return null
    }

    return URL.createObjectURL(blob)
  } catch (e) {
    console.error('TTS fetch error:', e)
    return null
  }
}

export async function playTts(
  text: string,
  chatId: string,
  settings: TtsSettings = {},
): Promise<void> {
  stopAudioQueue()
  resetAudioQueue()

  const chunks = splitIntoChunks(text)
  if (chunks.length === 0) return
  console.log(`TTS: playing ${chunks.length} chunks (voice=${settings.voice || 'default'})`)

  const results: Promise<string | null>[] = []
  let started = 0

  while (started < chunks.length && started < MAX_CONCURRENT_TTS) {
    results.push(fetchChunkAudio(chunks[started], chatId, settings))
    started++
  }

  for (let i = 0; i < chunks.length; i++) {
    const audio = await results[i]
    if (audio) {
      enqueueAudio(audio)
    }
    if (started < chunks.length) {
      results.push(fetchChunkAudio(chunks[started], chatId, settings))
      started++
    }
  }
}

export function stopTts(): void {
  stopAudioQueue()
}
